import type { PlanType } from "@/types/subscription.types";
import {
  type AnyPlan,
  type GateProduct,
  type GateSpec,
  FEATURE_PLAN_MAP,
  planAllows,
} from "@u/featureGating";
import { isSubscriptionBlocking } from "@/types/subscription.types";
import { useAuth } from "@h/useCtx";

/** Recurso opcional para resolver el plan del DUEÑO (el viewer público no
 *  tiene sesión: el plan llega por la fila del tour). */
export interface GateResource {
  /** id del recurso (tour) — para deep-link a su sección billing. */
  id?: string;
  /** Plan del dueño del tour (vía v_tours.owner_plan_type). */
  owner_plan_type?: PlanType | null;
}

export interface FeatureGateResult {
  allowed: boolean;
  product: GateProduct;
  requiredPlan: AnyPlan;
  /** Resolved effective plan (for numeric caps like the tour language limit). */
  plan: AnyPlan;
}

/**
 * Hook unificado de gating por plan. El plan es del USUARIO (no del recurso):
 *   1. Plan del dueño del tour, cuando se pasa el recurso (viewer público).
 *   2. Admin → permitido.
 *   3. Usuario normal → su suscripción.
 *
 * Sólo quedan gateados los ejes de MARCA + los topes numéricos; ninguna feature
 * se bloquea por plan (ver `featureGating.ts`).
 */
export function useFeatureGate(
  spec: GateSpec,
  resource?: GateResource | null,
): FeatureGateResult {
  const { subscription, isAdmin } = useAuth();

  const required = FEATURE_PLAN_MAP[spec.feature];
  let plan: PlanType;
  if (resource?.owner_plan_type) plan = resource.owner_plan_type;
  else if (isAdmin) plan = "agency";
  // Espeja get_owner_plan_type en SQL: sub bloqueante (unpaid / canceled
  // post-grace) resuelve a free.
  else
    plan = isSubscriptionBlocking(subscription)
      ? "free"
      : subscription!.plan_type;

  return {
    allowed: planAllows(plan, required),
    product: "360",
    requiredPlan: required,
    plan,
  };
}
